import React from "react";
import { Menu, UnstyledButton, Tooltip, Box } from "@mantine/core";
import {
  IconPlus,
  IconChevronDown,
  IconClipboardList,
  IconShoppingBag,
  IconPackage,
  IconGift,
} from "@tabler/icons-react";
import surfaceStyles from "../../../../common/surface/surface.module.css";
import styles from "./AddItemMenu.module.css";

interface AddItemMenuProps {
  onAddLineItem: (type: "service" | "product" | "package" | "giftcard") => void;
  /**
   * Whether the salon has any package/special configured. When false the
   * "Package or Special" item is disabled with a prompt to set one up in
   * Settings. Defaults to true so callers that don't gate it keep working.
   */
  hasPackagesConfigured?: boolean;
  /**
   * Whether to show the "Gift Card" item (Gift Cards feature wall). Defaults to
   * false so callers that don't opt in never surface it. The per-salon enabled
   * check + amount bounds are handled by the gift-card form itself.
   */
  giftCardsEnabled?: boolean;
}

/**
 * The single "Add" pill on Transaction Items — opens a dropdown offering
 * Service / Product / Package or Special (replaces the per-section Add buttons).
 */
export default function AddItemMenu({
  onAddLineItem,
  hasPackagesConfigured = true,
  giftCardsEnabled = false,
}: AddItemMenuProps) {
  return (
    <Menu
      position="bottom-end"
      width={230}
      radius={14}
      shadow="lg"
      withinPortal
    >
      <Menu.Target>
        <UnstyledButton
          className={`${surfaceStyles.pillButton} ${styles.addPill}`}
          aria-label="Add line item"
        >
          <IconPlus size={15} />
          Add
          <IconChevronDown size={14} stroke={2.2} />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown className={styles.dropdown}>
        <Menu.Item
          className={styles.item}
          onClick={() => onAddLineItem("service")}
          leftSection={<IconClipboardList size={16} />}
        >
          Service
        </Menu.Item>
        <Menu.Item
          className={styles.item}
          onClick={() => onAddLineItem("product")}
          leftSection={<IconShoppingBag size={16} />}
        >
          Product
        </Menu.Item>
        {/* Box wrapper keeps the tooltip hoverable even while the item is a
            disabled (pointer-events: none) Menu.Item. */}
        <Tooltip
          label="Create a package or special in Settings → Packages & Specials first."
          disabled={hasPackagesConfigured}
          multiline
          w={240}
          withArrow
          events={{ hover: true, focus: true, touch: true }}
        >
          <Box>
            <Menu.Item
              className={styles.item}
              onClick={() => onAddLineItem("package")}
              leftSection={<IconPackage size={16} />}
              disabled={!hasPackagesConfigured}
            >
              Package or Special
            </Menu.Item>
          </Box>
        </Tooltip>
        {giftCardsEnabled && (
          <Menu.Item
            className={styles.item}
            onClick={() => onAddLineItem("giftcard")}
            leftSection={<IconGift size={16} />}
          >
            Gift Card
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
